import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

export default function AdminPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/posts").then((r) => setPosts(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this post and all its comments?")) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Post deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (p) => {
    try {
      await api.put(`/admin/posts/${p.id}`, {
        title: p.title, slug: p.slug, excerpt: p.excerpt, content: p.content,
        category: p.category, cover_image: p.cover_image,
        status: p.status === "published" ? "draft" : "published",
      });
      toast.success(p.status === "published" ? "Moved to drafts" : "Published!");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-posts-page">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-emerald">Blog Posts</h1>
          <p className="text-brand-muted text-sm mt-1">{posts.length} post{posts.length !== 1 ? "s" : ""}</p>
        </div>
        <Link to="/admin/posts/new" data-testid="posts-new-btn" className="px-5 py-2.5 rounded-full bg-brand-emerald text-white text-sm font-bold hover:bg-brand-lime transition-colors flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Post
        </Link>
      </div>

      <div className="bg-white rounded-2xl border border-[#D6E4DC] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="posts-table">
            <thead>
              <tr className="bg-brand-mint text-brand-emerald text-left">
                <th className="px-4 py-3 font-bold">Title</th>
                <th className="px-4 py-3 font-bold">Category</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold">Created</th>
                <th className="px-4 py-3 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-brand-muted">Loading…</td></tr>
              ) : posts.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-brand-muted">No posts yet. Create your first one!</td></tr>
              ) : posts.map((p) => (
                <tr key={p.id} className="border-t border-[#EFF5F1] hover:bg-brand-mint/50" data-testid={`post-row-${p.slug}`}>
                  <td className="px-4 py-3 font-semibold text-brand-ink max-w-xs truncate">{p.title}</td>
                  <td className="px-4 py-3 text-xs">{p.category}</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`post-status-toggle-${p.slug}`}
                      onClick={() => toggleStatus(p)}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full transition-colors ${p.status === "published" ? "bg-green-100 text-green-800 hover:bg-green-200" : "bg-amber-100 text-amber-800 hover:bg-amber-200"}`}
                    >
                      {p.status}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-brand-muted">{new Date(p.created_at).toLocaleDateString("en-NG")}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      {p.status === "published" && (
                        <Link to={`/blog/${p.slug}`} target="_blank" data-testid={`post-view-${p.slug}`} className="p-2 rounded-lg text-brand-muted hover:bg-brand-mint hover:text-brand-emerald" aria-label="View post"><Eye className="w-4 h-4" /></Link>
                      )}
                      <Link to={`/admin/posts/${p.id}/edit`} data-testid={`post-edit-${p.slug}`} className="p-2 rounded-lg text-brand-muted hover:bg-brand-mint hover:text-brand-emerald" aria-label="Edit post"><Pencil className="w-4 h-4" /></Link>
                      <button onClick={() => remove(p.id)} data-testid={`post-delete-${p.slug}`} className="p-2 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600" aria-label="Delete post"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
